/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useNavigate } from '@/hooks/use-navigate';
import { routeHref, routes } from '@/route-paths';
import { cn } from '@/lib/utils';
import {
    buildApplePayBridgeUrl,
    completedOrderNumber,
    type ApplePayBridgeContext,
} from '../lib/native-apple-pay-bridge';

interface Props {
    context: ApplePayBridgeContext;
    sku?: string;
    className?: string;
}

interface ApplePayCapabilityWindow extends Window {
    ApplePaySession?: {
        canMakePayments(): boolean;
    };
}

/**
 * Hosts SFCC's native `<isapplepay>` output in a same-origin document. SFCC owns merchant
 * validation, Apple Pay callbacks, Cybersource authorization, and order placement; this component
 * deliberately never receives Apple credentials or payment-token data.
 */
export default function ApplePayButton({ context, sku, className }: Props) {
    const { site, locale, currency } = useSite();
    const navigate = useNavigate();
    const [browserEligible, setBrowserEligible] = useState(false);
    const [bridgeReady, setBridgeReady] = useState(false);
    const completed = useRef(false);
    const src = useMemo(
        () => buildApplePayBridgeUrl(site.id, locale.id, context, sku),
        [context, locale.id, site.id, sku]
    );

    useEffect(() => {
        try {
            const ApplePaySession = (window as ApplePayCapabilityWindow).ApplePaySession;
            setBrowserEligible(Boolean(window.isSecureContext && ApplePaySession?.canMakePayments()));
        } catch {
            setBrowserEligible(false);
        }
    }, []);

    useEffect(() => {
        setBridgeReady(false);
        completed.current = false;
    }, [src]);

    if (!browserEligible || currency !== 'USD' || (context === 'pdp' && !sku)) return null;

    return (
        <div
            aria-hidden={!bridgeReady}
            inert={!bridgeReady}
            className={cn(
                className,
                bridgeReady ? 'block' : 'pointer-events-none fixed -left-[10000px] top-0 w-80 opacity-0'
            )}>
            <iframe
                src={src}
                title={context === 'pdp' ? 'Buy with Apple Pay' : 'Pay with Apple Pay'}
                allow="payment"
                referrerPolicy="same-origin"
                className="h-11 min-h-11 w-full border-0"
                data-testid="cybersource-native-apple-pay-bridge"
                onLoad={(event) => {
                    const frame = event.currentTarget;
                    try {
                        const frameWindow = frame.contentWindow;
                        if (!frameWindow) {
                            setBridgeReady(false);
                            return;
                        }
                        const orderNo = completedOrderNumber(frameWindow.location.href, window.location.origin);
                        if (orderNo && !completed.current) {
                            completed.current = true;
                            setBridgeReady(false);
                            void navigate(routeHref(routes.orderConfirmation, { orderNo }));
                            return;
                        }
                        setBridgeReady(Boolean(frameWindow.document.querySelector('.dw-apple-pay-button')));
                    } catch {
                        // The bridge must remain same-origin. Fail closed if routing sends it elsewhere.
                        setBridgeReady(false);
                    }
                }}
            />
        </div>
    );
}
